import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Observable, of, Subscription, zip } from 'rxjs';
import { delay, distinctUntilChanged, filter, map, startWith, switchMap } from 'rxjs/operators';

import { ButtonMode, IconPosition } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { LoaderType } from '@fcom/common/interfaces';
import { CmsDataService } from '@fcom/core/services';
import { finShare, safeMap as safeMapObs } from '@fcom/rx';
import { isPresent, stopPropagation, unsubscribe } from '@fcom/core/utils';
import { CmsSeatDetails } from '@fcom/common/interfaces/booking';

import { ButtonState, SeatDetails } from '../../../interfaces';

const POPOVER_ANIMATION_DURATION = 300;

@Component({
  selector: 'fin-seat-details',
  styleUrls: ['./seat-details.component.scss'],
  templateUrl: './seat-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeatDetailsComponent implements OnInit, OnDestroy {
  public readonly LoaderType = LoaderType;
  public readonly ButtonState = ButtonState;
  public readonly IconPosition = IconPosition;
  public readonly ButtonMode = ButtonMode;
  public readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input()
  seatDetails$: Observable<SeatDetails>;

  @Input()
  hideButtons = false;

  /**
   * Emits when the seat details are loaded
   * @type {EventEmitter<SeatDetails>}
   */
  @Output()
  seatDetailsLoadComplete: EventEmitter<void> = new EventEmitter<void>();

  /**
   * Emits when the seat has been selected for the traveler
   * @type {EventEmitter<SeatDetails>}
   */
  @Output()
  seatSelected: EventEmitter<SeatDetails> = new EventEmitter<SeatDetails>();

  /**
   * Emits when the seat should be cleared
   * @type {EventEmitter<SeatDetails>}
   */
  @Output()
  seatCleared: EventEmitter<SeatDetails> = new EventEmitter<SeatDetails>();

  cmsSeatDetails$: Observable<CmsSeatDetails>;
  seatTitle$: Observable<string>;
  seatDetailsWithCMS$: Observable<SeatDetails | any>;
  seatDetailsSubscription = new Subscription();

  constructor(
    private languageService: LanguageService,
    private cmsDataService: CmsDataService
  ) {}

  ngOnInit(): void {
    this.cmsSeatDetails$ = this.seatDetails$.pipe(
      safeMapObs((s: SeatDetails) => s.seatType),
      distinctUntilChanged(),
      switchMap((seatType: string) => this.languageService.translate(['seatTypes', seatType])),
      map((jsonData: any) => jsonData?.url),
      switchMap((url: string) =>
        isPresent(url)
          ? this.cmsDataService.getFragmentJson<CmsSeatDetails>(url).pipe(startWith(null as CmsSeatDetails))
          : of({} as CmsSeatDetails)
      ),
      finShare()
    );

    this.seatTitle$ = this.cmsSeatDetails$.pipe(
      switchMap((d: CmsSeatDetails) => {
        if (!d || d.teaserTitle) {
          return of(d?.teaserTitle);
        }
        return this.seatDetails$.pipe(map((s) => s.seatType));
      })
    );

    this.seatDetailsWithCMS$ = zip(
      this.seatDetails$.pipe(delay(POPOVER_ANIMATION_DURATION)),
      this.cmsSeatDetails$.pipe(filter(Boolean))
    );

    this.seatDetailsSubscription.add(
      this.seatDetailsWithCMS$.subscribe(() => {
        this.seatDetailsLoadComplete.emit();
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.seatDetailsSubscription);
  }

  selectSeat(details: SeatDetails, event: Event): void {
    stopPropagation(event);
    this.seatSelected.emit(details);
  }

  clearSeat(details: SeatDetails, event: Event): void {
    stopPropagation(event);
    this.seatCleared.emit(details);
  }

  confirmSeatClick(details: SeatDetails, event: Event, forceSelection = false): false {
    if (details.seatSelectedForTraveler === details.travelerId && !forceSelection) {
      this.seatCleared.emit(details);
    } else {
      this.seatSelected.emit(details);
    }
    return stopPropagation(event);
  }
}
